/**
 * `defer-regex-server-telemetry` — request/response shapes for the
 * no-code regex picker telemetry surface.
 *
 * Three endpoints, three pairs of types:
 *
 *   POST /api/regex-telemetry          → record a single use
 *   GET  /api/regex-telemetry          → per-org "what we use" view
 *   GET  /api/admin/regex-telemetry    → cross-org pattern popularity
 *
 * The server NEVER returns custom pattern *text* across orgs — only
 * preset ids + pattern hashes. The per-org view does include raw
 * pattern text since the requesting admin owns those rows.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------
// POST /api/regex-telemetry
// ---------------------------------------------------------------------

export const RecordRegexUsageInputSchema = z.object({
  /**
   * Preset id from the no-code picker library (e.g. `otp-6-digit`).
   * Omit when the user typed a custom pattern that doesn't match any
   * registered preset.
   */
  presetId: z.string().min(1).max(64).optional(),
  /**
   * Raw regex pattern source. Required even when `presetId` is set —
   * the server uses it to compute the hash and lets us reconcile
   * preset drift (the picker's preset might have been edited since
   * the user picked it).
   */
  pattern: z.string().min(1).max(4096),
  /** Regex flags as a JavaScript flag string ("i", "im", …). */
  flags: z.string().max(16).optional(),
});

export type RecordRegexUsageInput = z.infer<typeof RecordRegexUsageInputSchema>;

export const RecordRegexUsageResponseSchema = z.object({
  ok: z.literal(true),
  /**
   * Total count for this (org, pattern, flags) row AFTER the write.
   * Lets the picker show a tiny "used N times in this org" hint
   * without a second round-trip.
   */
  usageCount: z.number().int().nonnegative(),
});

export type RecordRegexUsageResponse = z.infer<typeof RecordRegexUsageResponseSchema>;

// ---------------------------------------------------------------------
// GET /api/regex-telemetry — per-org view
// ---------------------------------------------------------------------

export const OrgRegexUsageRowSchema = z.object({
  /** Preset id if the entry matches one of the picker library entries. */
  presetId: z.string().nullable(),
  /** Pattern source — only ever set for rows owned by the requesting org. */
  pattern: z.string(),
  flags: z.string(),
  isPreset: z.boolean(),
  usageCount: z.number().int().nonnegative(),
  firstSeenAt: z.string(),
  lastUsedAt: z.string(),
});

export type OrgRegexUsageRow = z.infer<typeof OrgRegexUsageRowSchema>;

export const OrgRegexUsageResponseSchema = z.object({
  rows: z.array(OrgRegexUsageRowSchema),
  /** Total distinct (pattern, flags) pairs the org has used. */
  totalDistinct: z.number().int().nonnegative(),
});

export type OrgRegexUsageResponse = z.infer<typeof OrgRegexUsageResponseSchema>;

// ---------------------------------------------------------------------
// GET /api/admin/regex-telemetry — cross-org view (platform admin only)
// ---------------------------------------------------------------------

export const RegexPresetPopularityRowSchema = z.object({
  presetId: z.string(),
  /** Number of distinct orgs that have used this preset at least once. */
  orgCount: z.number().int().nonnegative(),
  /** Sum of usage_count across all orgs. */
  totalUses: z.number().int().nonnegative(),
});

export type RegexPresetPopularityRow = z.infer<typeof RegexPresetPopularityRowSchema>;

export const RegexCustomPopularityRowSchema = z.object({
  /** SHA-256 hex of the pattern — safe to share across orgs. */
  patternHash: z.string(),
  flags: z.string(),
  /**
   * Number of distinct orgs that have used this exact (hash, flags)
   * pair. Threshold-gated in the API: rows with `orgCount < 2` are
   * filtered out so a single org typing an embarrassing regex once
   * never leaks anywhere.
   */
  orgCount: z.number().int().nonnegative(),
  totalUses: z.number().int().nonnegative(),
});

export type RegexCustomPopularityRow = z.infer<typeof RegexCustomPopularityRowSchema>;

export const AdminRegexTelemetryResponseSchema = z.object({
  presets: z.array(RegexPresetPopularityRowSchema),
  customPatterns: z.array(RegexCustomPopularityRowSchema),
  /** Total distinct patterns currently in the table. */
  totalDistinctPatterns: z.number().int().nonnegative(),
  /** Number of orgs that have recorded at least one regex use. */
  totalOrgs: z.number().int().nonnegative(),
});

export type AdminRegexTelemetryResponse = z.infer<typeof AdminRegexTelemetryResponseSchema>;
